import { T, Var } from 'gt-react';
import {
  calculateDiscountPercent,
  formatCurrency,
} from 'src/pages/authenticated/sales-orders/components/line-item-table/priceUtils';

function toNum(value: string | number | null | undefined): number | null {
  if (value == null) return null;
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(n) ? n : null;
}

/**
 * The one-line signed delta of a PO price against list — dollars first, percent
 * in parentheses. Below-list reads red (a concession), above-list green, on-list
 * muted. Renders nothing when there is no PO price to compare.
 */
export function PriceDeltaLine({
  unitPrice,
  listPrice,
  currency,
}: {
  unitPrice: string | number | null | undefined;
  listPrice: string | number | null | undefined;
  currency?: string | null;
}) {
  const po = toNum(unitPrice);
  const list = toNum(listPrice);

  if (po == null) return null;

  if (list == null) {
    return (
      <span className="text-muted-foreground">
        <T>no list price</T>
      </span>
    );
  }
  const off = list - po;
  const pct = calculateDiscountPercent(po, list);
  if (Math.abs(off) <= 0.005) {
    return (
      <span className="text-muted-foreground">
        <T>at list</T>
      </span>
    );
  }
  if (off > 0) {
    // Below list — customer is paying less than list (a discount).
    return (
      <span className="text-destructive">
        {pct != null ? (
          <T>
            −<Var>{formatCurrency(off, currency)}</Var> vs list (
            <Var>{Math.round(pct)}</Var>%)
          </T>
        ) : (
          <T>
            −<Var>{formatCurrency(off, currency)}</Var> vs list
          </T>
        )}
      </span>
    );
  }
  // Above list — customer is paying a premium over list.
  return (
    <span className="text-emerald-700">
      <T>
        +<Var>{formatCurrency(Math.abs(off), currency)}</Var> vs list
      </T>
    </span>
  );
}

/**
 * Unit-price cell (spec §7.2): the customer's PO price with a one-line signed
 * delta against list. We never stack two raw price numbers.
 */
export function DeltaPriceCell({
  unitPrice,
  listPrice,
  currency,
}: {
  unitPrice: string | number | null | undefined;
  listPrice: string | number | null | undefined;
  currency?: string | null;
}) {
  const po = toNum(unitPrice);

  if (po == null) {
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <div className="flex flex-col">
      <span className="tabular-nums">{formatCurrency(po, currency)}</span>
      <span className="text-[11px]">
        <PriceDeltaLine
          unitPrice={unitPrice}
          listPrice={listPrice}
          currency={currency}
        />
      </span>
    </div>
  );
}
